import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useRef } from 'react';
import { PlaylistModule } from '@/store/playlist/types';
import { TemplateLightHeader } from './TemplateLightHeader';
import { DarkMembersEpisodeCard } from './DarkMembersEpisodeCard';
import { TemplateDarkMembersFooter } from './TemplateDarkMembersFooter';
import hostVslThumbnail from '@/assets/hostvsl-thumbnail-placeholder.jpg';
import logoHostVslWhite from '@/assets/logo-hostvsl-white.png';
import { useDarkMembersProgress } from '@/hooks/useDarkMembersProgress';
import DOMPurify from 'dompurify';
import { ExternalLink } from 'lucide-react';

interface DarkMembersPandaViewProps {
  module: PlaylistModule;
  onBack?: () => void;
}

export function DarkMembersPandaView({ 
  module,
  onBack,
}: DarkMembersPandaViewProps) {
  useLanguage();
  const sortedLessons = [...module.lessons].sort((a, b) => a.order - b.order);
  const videoRef = useRef<HTMLVideoElement>(null);
  const saveIntervalRef = useRef<NodeJS.Timeout>();
  
  const {
    updateLessonProgress,
    markLessonComplete,
    getLessonProgress,
    getLastLesson,
    getAllLessonsProgress,
  } = useDarkMembersProgress(module.id);

  // Initialize with last watched lesson or first lesson
  const [selectedLesson, setSelectedLesson] = useState(() => {
    const lastLesson = getLastLesson();
    if (lastLesson) {
      const index = sortedLessons.findIndex(l => l.id === lastLesson.lessonId);
      return index >= 0 ? index : 0;
    }
    return 0;
  });

  const [showNextOverlay, setShowNextOverlay] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [hasRestoredPosition, setHasRestoredPosition] = useState(false);
  
  const allProgress = getAllLessonsProgress();

  // Next lesson logic
  const hasNextLesson = selectedLesson < sortedLessons.length - 1;
  const nextLesson = hasNextLesson ? sortedLessons[selectedLesson + 1] : null;
  const nextLessonIndex = selectedLesson + 1;
  const currentLesson = sortedLessons[selectedLesson];

  // Go to next lesson
  const goToNextLesson = () => {
    if (selectedLesson < sortedLessons.length - 1) {
      // Save current progress before advancing
      if (videoRef.current) {
        markLessonComplete(currentLesson.id, videoRef.current.duration);
      }
      
      setSelectedLesson(prev => prev + 1);
      setHasRestoredPosition(false);
    }
    setShowNextOverlay(false);
    setCountdown(5);
  };

  // Handle lesson click
  const handleLessonClick = (index: number) => {
    // Save current progress before switching
    if (videoRef.current && !videoRef.current.paused) {
      const video = videoRef.current;
      updateLessonProgress(currentLesson.id, video.currentTime, video.duration);
    }
    
    setSelectedLesson(index);
    setShowNextOverlay(false);
    setHasRestoredPosition(false);
  };

  // Handle video ended
  const handleVideoEnded = () => {
    // Mark lesson as complete
    if (videoRef.current) {
      markLessonComplete(currentLesson.id, videoRef.current.duration);
    }

    // Se ainda há aulas neste módulo, mostra overlay de próxima aula
    if (selectedLesson < sortedLessons.length - 1) {
      setShowNextOverlay(true);
    }
    // Se não há mais aulas, NADA acontece
    // O usuário escolhe voltar manualmente
  };

  // Cancel autoplay
  const cancelAutoplay = () => {
    setShowNextOverlay(false);
    setCountdown(5);
  };

  // Restore video position when lesson loads
  useEffect(() => {
    const video = videoRef.current;
    if (!video || hasRestoredPosition) return;

    const handleLoadedMetadata = () => {
      const progress = getLessonProgress(currentLesson.id);
      if (progress && progress.watchTime > 0 && progress.watchTime < progress.totalDuration - 5) {
        video.currentTime = progress.watchTime;
      }
      setHasRestoredPosition(true);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [currentLesson.id, hasRestoredPosition, getLessonProgress]);

  // Auto-save progress every 3 seconds during playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      saveIntervalRef.current = setInterval(() => {
        if (!video.paused && video.duration) {
          updateLessonProgress(currentLesson.id, video.currentTime, video.duration);
        }
      }, 3000);
    };

    const handlePause = () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
      if (video.duration) {
        updateLessonProgress(currentLesson.id, video.currentTime, video.duration);
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [currentLesson.id, updateLessonProgress]);

  // Save progress when leaving page or switching tabs
  useEffect(() => {
    const handleBeforeUnload = () => {
      const video = videoRef.current;
      if (video && video.duration) {
        updateLessonProgress(currentLesson.id, video.currentTime, video.duration);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        const video = videoRef.current;
        if (video && video.duration) {
          updateLessonProgress(currentLesson.id, video.currentTime, video.duration);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentLesson.id, updateLessonProgress]);

  // Countdown timer for lessons
  useEffect(() => {
    if (!showNextOverlay) return;
    
    if (countdown <= 0) {
      goToNextLesson();
      return;
    }
    
    const timer = setTimeout(() => {
      setCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [showNextOverlay, countdown]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div data-theme-isolated className="h-screen flex flex-col bg-[#0E0E0E] overflow-hidden">
      {/* Header Premium 64px - Escuro */}
      <header 
        className="h-16 px-6 flex items-center sticky top-0 z-50"
        style={{ 
          backgroundColor: '#0E0E0E',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <div className="flex items-center gap-4 w-full">
          {/* Logo HostVSL */}
          <a 
            href="https://www.hostvsl.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity flex-shrink-0"
          >
            <img 
              src={logoHostVslWhite} 
              alt={translateInterfaceText("HostVSL")}
              className="h-6 w-auto"
            />
          </a>
          
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-sm overflow-hidden flex-1">
            {/* Home - Cinza clicável */}
            <button
              onClick={onBack}
              className="hover:text-[rgba(255,255,255,0.7)] transition-colors truncate flex-shrink-0"
              style={{ color: 'rgba(255,255,255,0.5)' }}
            >
              {translateInterfaceText("Home")}</button>
            
            {/* Separador */}
            <span style={{ color: 'rgba(255,255,255,0.3)' }} className="flex-shrink-0">›</span>
            
            {/* Módulo - Cinza (oculto no mobile) */}
            <span 
              className="hidden md:inline truncate max-w-[200px]" 
              style={{ color: 'rgba(255,255,255,0.5)' }}
            >
              {module.title}
            </span>
            <span 
              className="hidden md:inline flex-shrink-0" 
              style={{ color: 'rgba(255,255,255,0.3)' }}
            >
              ›
            </span>
            
            {/* Aula Atual - Branco (selecionada) */}
            <span className="text-white font-medium truncate">
              {currentLesson?.title}
            </span>
          </nav>
        </div>
      </header>

      {/* Container Principal - Desktop */}
      <div className="hidden lg:flex flex-1 flex-col overflow-hidden">
        <div className="w-full max-w-[1400px] mx-auto px-10 py-6 flex gap-6 flex-1 overflow-hidden">
          {/* Player Container - 65% */}
          <div className="flex-[0_0_65%] max-w-[900px] overflow-y-auto main-content-scroll pr-4">
            {/* Player */}
            <div 
              className="relative aspect-video bg-gray-900 rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
            >
              <video
                ref={videoRef}
                key={selectedLesson}
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
                src={`https://example.com/videos/${currentLesson?.videoId}.mp4`}
                onEnded={handleVideoEnded}
              >
                {translateInterfaceText("Seu navegador não suporta vídeos.")}</video>


              {/* Overlay de Próximo Vídeo */}
              {showNextOverlay && nextLesson && (
                <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center z-10 backdrop-blur-sm">
                  <p className="text-white/60 text-sm mb-2">{translateInterfaceText("Próxima aula em")}</p>
                  
                  {/* Countdown circular */}
                  <div className="relative w-20 h-20 mb-4">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" stroke="white" strokeWidth="3" fill="none" opacity="0.2" />
                      <circle
                        cx="50" cy="50" r="45"
                        stroke="#3B82F6" strokeWidth="3" fill="none"
                        strokeDasharray={`${2 * Math.PI * 45}`}
                        strokeDashoffset={`${2 * Math.PI * 45 * (1 - countdown / 5)}`}
                        className="transition-all duration-1000 ease-linear"
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-white text-3xl font-bold">
                      {countdown}
                    </span>
                  </div>
                  
                  {/* Info da próxima aula */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-24 h-14 bg-[#0B1423] rounded-lg flex items-center justify-center overflow-hidden">
                      {nextLesson.thumbnailUrl ? (
                        <img src={nextLesson.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <img src={logoHostVslWhite} alt="" className="w-12 opacity-60" />
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-white/60 text-xs">{translateInterfaceText("Aula")}{" "}{nextLessonIndex + 1}</p>
                      <p className="text-white font-semibold text-sm max-w-[200px] truncate">
                        {nextLesson.title}
                      </p>
                    </div>
                  </div>
                  
                  {/* Botões */}
                  <div className="flex gap-3">
                    <button
                      onClick={cancelAutoplay}
                      className="px-6 py-2.5 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
                    >
                      {translateInterfaceText("Cancelar")}</button>
                    <button
                      onClick={goToNextLesson}
                      className="px-6 py-2.5 rounded-full bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] transition-colors"
                    >
                      {translateInterfaceText("Reproduzir agora")}</button>
                  </div>
                </div>
              )}
            </div>

            {/* Área de Informações - Alinhada com o Player */}
            <div className="mt-6 mb-6">
              {/* Título do Episódio - H1 Premium */}
              {currentLesson?.title && (
                <h1 className="text-[20px] font-semibold text-white">
                  {currentLesson.title}
                </h1>
              )}
              
              {/* Descrição - Texto Normal */}
            {currentLesson?.description && (
              <div 
                className="text-[15px] leading-relaxed mt-3 lesson-description"
                style={{ color: 'rgba(255,255,255,0.7)' }}
                dangerouslySetInnerHTML={{ __html: currentLesson.description }}
              />
            )}

            {/* CTA Button - Abaixo da descrição */}
            {currentLesson?.ctaButton?.enabled && currentLesson.ctaButton.text && (
              <div className="mt-6">
                <a
                  href={currentLesson.ctaButton.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-3 
                             bg-[#3B82F6] hover:bg-[#2563EB] text-white 
                             font-semibold text-base rounded-full 
                             transition-all duration-200 hover:scale-105
                             shadow-[0_4px_14px_rgba(59,130,246,0.4)]"
                >
                  {currentLesson.ctaButton.text}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            )}
            
            <style>{`
              .lesson-description p { margin-bottom: 12px; line-height: 1.6; }
              .lesson-description strong { color: #FFFFFF; font-weight: 600; }
              .lesson-description ul { margin: 12px 0; padding-left: 20px; list-style-type: disc; }
              .lesson-description li { margin-bottom: 6px; }
              .lesson-description a { color: #3B82F6; text-decoration: underline; }
              .lesson-description a:hover { color: #60A5FA; }
            `}</style>
            </div>
          </div>

          {/* Sidebar - Alinhada com o player */}
          <aside 
            className="flex flex-col"
            style={{ 
              minWidth: '360px',
              maxWidth: '390px',
              width: '360px',
            }}
          >
            <div 
              className="flex flex-col overflow-hidden"
              style={{
                backgroundColor: '#141414',
                borderRadius: '14px',
                boxShadow: '0px 4px 22px rgba(0, 0, 0, 0.25)',
                border: '1px solid rgba(255,255,255,0.08)',
                height: 'auto',
                minHeight: '750px',
                maxHeight: 'calc(100vh - 180px)',
              }}
            >
              {/* Header Fixo */}
              <div className="flex-shrink-0 p-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <h2 className="text-[17px] font-semibold text-white mb-2">
                  {module.title}
                </h2>
                <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {sortedLessons.length} {sortedLessons.length === 1 ? translateInterfaceText("aula") : translateInterfaceText("aulas")}
                </p>
              </div>

              {/* Lista de Aulas - Com scroll */}
              <div 
                className="flex-1 overflow-y-auto sidebar-scroll"
                style={{ 
                  padding: '12px 0 4px 0',
                  maxHeight: '580px'
                }}
              >
                {sortedLessons.map((lesson, index) => {
                  const progress = allProgress[lesson.id];
                  return (
                    <DarkMembersEpisodeCard
                      key={lesson.id}
                      index={index}
                      title={lesson.title}
                      duration={formatDuration(lesson.duration)}
                      thumbnail={lesson.thumbnailUrl || hostVslThumbnail}
                      isPlaying={selectedLesson === index}
                      isWatched={progress?.completed || false}
                      progress={progress?.percentage || 0}
                      onClick={() => handleLessonClick(index)}
                    />
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Layout Mobile/Tablet */}
      <div className="lg:hidden flex-1 overflow-y-auto">
        {/* Player */}
        <div className="p-4">
          <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
            <video
              ref={videoRef}
              key={selectedLesson}
              className="w-full h-full object-contain"
              controls
              autoPlay
              playsInline
              src={`https://example.com/videos/${currentLesson?.videoId}.mp4`}
              onEnded={handleVideoEnded}
            >
              {translateInterfaceText("Seu navegador não suporta vídeos.")}</video>

            {/* Overlay Mobile */}
            {showNextOverlay && nextLesson && (
              <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center z-10 backdrop-blur-sm">
                <p className="text-white/60 text-xs mb-2">{translateInterfaceText("Próxima aula em")}</p>
                
                <div className="relative w-16 h-16 mb-3">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" stroke="white" strokeWidth="3" fill="none" opacity="0.2" />
                    <circle
                      cx="50" cy="50" r="45"
                      stroke="#3B82F6" strokeWidth="3" fill="none"
                      strokeDasharray={`${2 * Math.PI * 45}`}
                      strokeDashoffset={`${2 * Math.PI * 45 * (1 - countdown / 5)}`}
                      className="transition-all duration-1000 ease-linear"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-white text-2xl font-bold">
                    {countdown}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-20 h-12 bg-[#0B1423] rounded-lg flex items-center justify-center overflow-hidden">
                    {nextLesson.thumbnailUrl ? (
                      <img src={nextLesson.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <img src={logoHostVslWhite} alt="" className="w-10 opacity-60" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-white/60 text-[10px]">{translateInterfaceText("Aula")}{" "}{nextLessonIndex + 1}</p>
                    <p className="text-white font-semibold text-xs max-w-[150px] truncate">
                      {nextLesson.title}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={cancelAutoplay}
                    className="px-5 py-2 rounded-full bg-white/10 text-white text-xs font-medium hover:bg-white/20 transition-colors"
                  >
                    {translateInterfaceText("Cancelar")}</button>
                  <button
                    onClick={goToNextLesson}
                    className="px-5 py-2 rounded-full bg-[#3B82F6] text-white text-xs font-medium hover:bg-[#2563EB] transition-colors"
                  >
                    {translateInterfaceText("Reproduzir agora")}</button>
                </div>
              </div>
            )}
          </div>

          {/* Área de Informações Mobile - Alinhada com o Player */}
          <div className="px-4 mt-6 mb-6">
            {/* Título do Episódio - H1 Premium */}
            {currentLesson?.title && (
              <h1 className="text-[18px] font-semibold text-white">
                {currentLesson.title}
              </h1>
            )}
            
            {/* Descrição - Texto Normal */}
            {currentLesson?.description && (
              <p className="text-[14px] leading-relaxed mt-3" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {currentLesson.description}
              </p>
            )}
          </div>
        </div>

        {/* Título do Módulo Mobile */}
        <div className="px-4 pb-3 pt-2">
          <h3 className="text-[16px] font-semibold text-white mb-1">
            {module.title}
          </h3>
          <p className="text-[12px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {sortedLessons.length} {sortedLessons.length === 1 ? translateInterfaceText("aula") : translateInterfaceText("aulas")}
          </p>
        </div>

        {/* Lista de Aulas Mobile */}
        <div className="px-2 pb-4">
          {sortedLessons.map((lesson, index) => {
            const progress = allProgress[lesson.id];
            return (
              <DarkMembersEpisodeCard
                key={lesson.id}
                index={index}
                title={lesson.title}
                duration={formatDuration(lesson.duration)}
                thumbnail={lesson.thumbnailUrl || hostVslThumbnail}
                isPlaying={selectedLesson === index}
                isWatched={progress?.completed || false}
                progress={progress?.percentage || 0}
                onClick={() => handleLessonClick(index)}
              />
            );
          })}
        </div>
      </div>

      {/* Footer Premium Escuro */}
      <TemplateDarkMembersFooter 
        showTerms={true}
        showPrivacy={true}
      />

      {/* CSS para scrollbar escura e descrição HTML */}
      <style>{`
        /* Scrollbar da sidebar de aulas */
        .sidebar-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .sidebar-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.05);
          border-radius: 100px;
        }
        .sidebar-scroll::-webkit-scrollbar-thumb {
          background: #3D3D3D;
          border-radius: 100px;
        }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: #525252;
        }
        
        /* Firefox support */
        .sidebar-scroll {
          scrollbar-width: thin;
          scrollbar-color: #3D3D3D transparent;
        }

        /* Scrollbar da área principal do player */
        .main-content-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .main-content-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.03);
          border-radius: 100px;
        }
        .main-content-scroll::-webkit-scrollbar-thumb {
          background: #3D3D3D;
          border-radius: 100px;
        }
        .main-content-scroll::-webkit-scrollbar-thumb:hover {
          background: #525252;
        }
        
        /* Firefox support */
        .main-content-scroll {
          scrollbar-width: thin;
          scrollbar-color: #3D3D3D rgba(255,255,255,0.03);
        }
        
        .episode-description p {
          margin-bottom: 12px;
        }
        .episode-description strong,
        .episode-description b {
          font-weight: 600;
        }
        .episode-description a {
          color: #2D7BFF;
          text-decoration: none;
        }
        .episode-description a:hover {
          text-decoration: underline;
        }
        .episode-description ul,
        .episode-description ol {
          margin-left: 20px;
          margin-bottom: 12px;
        }
        .episode-description li {
          margin-bottom: 6px;
        }
        .episode-description h1,
        .episode-description h2,
        .episode-description h3 {
          font-weight: 600;
          margin-bottom: 8px;
        }
      `}</style>
    </div>
  );
}
